import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import PricingLicenseCard from '~/components/legacy/PricingLicenseCard.jsx';
import { trackCatalogEvent } from '~/modules/catalogEventTracking.js';
import {
  allQuestionsAnswered,
  buildConfigurationPayload,
  findSelectedOption,
  formatProjectLicensePrice,
  getCoverageScenarioStatus,
  getEstimatedPriceCents,
  getMaxUnlockedWizardStepIndex,
  getSelectedOptions,
  normalizeScenario,
  optionPriceLabel,
  questionIndex
} from '~/components/legacy/projectLicenseWizard.js';
import { parseReactProps } from '~/components/legacy/reactProps.js';

class PricingProjectLicenseFlow extends Component {
  static CONFIGURATOR_SCROLL_OFFSET = 100;
  static MOBILE_QUESTION_SCROLL_OFFSET = 76;
  static MOBILE_SELECTION_SCROLL_OFFSET = 90;
  static MOBILE_BREAKPOINT = 767;

  constructor(props) {
    super(props);

    this.configuratorRef = React.createRef();
    this.questionCardRef = React.createRef();
    this.familyCardRefs = {};
    this.trackedStartedFamilyKeys = new Set();
    this.trackedCompletedFamilyKeys = new Set();

    this.state = {
      activeFamilyKey: null,
      answersByFamily: {},
      wizardStepIndexByFamily: {},
      presetNameByFamily: {},
      configurationByFamily: {},
      saveStateByFamily: {},
      savedPresetByFamily: {},
      publicConfigurationByFamily: {},
      publicConfigurationStateByFamily: {},
      authPromptFamilyKey: null,
      latestSavedPreset: null
    };
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.activeFamilyKey !== this.state.activeFamilyKey && this.state.activeFamilyKey) {
      this.scrollConfiguratorIntoView();
    }
  }

  scrollConfiguratorIntoView() {
    if (typeof window === 'undefined') {
      return;
    }

    const isMobileViewport = typeof window.innerWidth === 'number' && window.innerWidth <= PricingProjectLicenseFlow.MOBILE_BREAKPOINT;
    const target = isMobileViewport && this.questionCardRef.current ? this.questionCardRef.current : this.configuratorRef.current;
    const offset = isMobileViewport ? PricingProjectLicenseFlow.MOBILE_QUESTION_SCROLL_OFFSET : PricingProjectLicenseFlow.CONFIGURATOR_SCROLL_OFFSET;

    if (!target) {
      return;
    }

    if (typeof target.getBoundingClientRect === 'function' && typeof window.scrollTo === 'function') {
      const rect = target.getBoundingClientRect();
      const scrollTop = Math.max((window.scrollY || window.pageYOffset || 0) + rect.top - offset, 0);

      window.scrollTo({ top: scrollTop, behavior: 'smooth' });
      return;
    }

    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  scrollFamilyCardIntoView(familyKey) {
    if (typeof window === 'undefined') {
      return;
    }

    const isMobileViewport = typeof window.innerWidth === 'number' && window.innerWidth <= PricingProjectLicenseFlow.MOBILE_BREAKPOINT;
    if (!isMobileViewport) {
      return;
    }

    const target = this.familyCardRefs[familyKey]?.current;
    if (!target) {
      return;
    }

    if (typeof target.getBoundingClientRect === 'function' && typeof window.scrollTo === 'function') {
      const rect = target.getBoundingClientRect();
      const scrollTop = Math.max((window.scrollY || window.pageYOffset || 0) + rect.top - PricingProjectLicenseFlow.MOBILE_SELECTION_SCROLL_OFFSET, 0);

      window.scrollTo({ top: scrollTop, behavior: 'smooth' });
      return;
    }

    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  familyByKey(familyKey) {
    return Array.isArray(this.props.families) ? this.props.families.find((family) => family.key === familyKey) || null : null;
  }

  activeFamily() {
    return this.familyByKey(this.state.activeFamilyKey);
  }

  answersForFamily(familyKey) {
    return this.state.answersByFamily[familyKey] || {};
  }

  presetNameForFamily(family) {
    if (Object.prototype.hasOwnProperty.call(this.state.presetNameByFamily, family.key)) {
      return this.state.presetNameByFamily[family.key];
    }

    return this.buildPresetName(family.title);
  }

  savedPresetsForFamily(familyKey) {
    const savedPresetsByFamily = this.props.savedPresetsByFamily || {};
    const savedPresets = Array.isArray(savedPresetsByFamily[familyKey]) ? savedPresetsByFamily[familyKey] : [];
    const justSavedPreset = this.state.savedPresetByFamily[familyKey];

    if (!justSavedPreset) {
      return savedPresets;
    }

    return [justSavedPreset, ...savedPresets.filter((preset) => preset.id !== justSavedPreset.id)];
  }

  justSavedPresetForFamily(familyKey) {
    const inMemoryPreset = this.state.savedPresetByFamily[familyKey];
    if (inMemoryPreset) {
      return inMemoryPreset;
    }

    const justSavedPresetId = this.props.justSavedPresetId;
    if (!justSavedPresetId) {
      return null;
    }

    return this.savedPresetsForFamily(familyKey).find((preset) => String(preset.id) === String(justSavedPresetId)) || null;
  }

  i18nValue(key) {
    if (this.props.i18n && this.props.i18n[key] !== undefined) {
      return this.props.i18n[key];
    }

    return PricingProjectLicenseFlow.defaultProps.i18n[key];
  }

  csrfToken() {
    if (typeof document === 'undefined') {
      return '';
    }

    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }

  currentReturnTo() {
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }

    const searchParams = new URLSearchParams(window.location.search || '');
    searchParams.delete('just_saved_preset_id');

    const search = searchParams.toString();
    return `${window.location.pathname}${search ? `?${search}` : ''}`;
  }

  latestSavedPreset() {
    if (this.state.latestSavedPreset) {
      return this.state.latestSavedPreset;
    }

    const justSavedPresetId = this.props.justSavedPresetId;
    if (!justSavedPresetId) {
      return null;
    }

    return Object.values(this.props.savedPresetsByFamily || {})
      .flat()
      .find((preset) => String(preset.id) === String(justSavedPresetId)) || null;
  }

  buildPresetName(familyTitle) {
    if (!familyTitle) {
      return '';
    }

    return this.props.locale === 'de' ? `Meine ${familyTitle}-Projektlizenz` : `My ${familyTitle} project license`;
  }

  selectedOptionsForFamily(family) {
    return getSelectedOptions(family, this.answersForFamily(family.key));
  }

  selectedOptionKeysForFamily(family) {
    return this.selectedOptionsForFamily(family).map((option) => option.key);
  }

  estimatedPriceCentsForFamily(family) {
    return getEstimatedPriceCents(family, this.answersForFamily(family.key));
  }

  allQuestionsAnsweredForFamily(family) {
    return allQuestionsAnswered(family, this.answersForFamily(family.key));
  }

  maxUnlockedWizardStepIndexForFamily(family) {
    return getMaxUnlockedWizardStepIndex(family, this.answersForFamily(family.key), { skipWizardResultStep: false });
  }

  currentWizardStepIndexForFamily(family) {
    const currentIndex = this.state.wizardStepIndexByFamily[family.key] || 0;
    return Math.min(currentIndex, this.maxUnlockedWizardStepIndexForFamily(family));
  }

  currentQuestionForFamily(family) {
    const stepIndex = this.currentWizardStepIndexForFamily(family);
    if (stepIndex >= family.questions.length) {
      return null;
    }

    return family.questions[stepIndex] || null;
  }

  selectedAnswerTag(option) {
    const shortName = String(option.short_name || '').trim();
    const fallbackLabel = String(option.label || '')
      .replace(/^Ja,\s*/i, '')
      .replace(/^Nein,\s*/i, '')
      .trim();
    const label = shortName || fallbackLabel || String(option.summary || '').trim();
    const isUpgrade = Number(option.price_delta_cents || 0) > 0 || Number(option.price_multiplier || 1) > 1;

    return {
      key: option.key,
      label,
      style: isUpgrade ? 'upgrade' : 'base'
    };
  }

  readableSelectedUsageItems(family) {
    return this.selectedOptionsForFamily(family).map((option) => String(option.summary || option.label || option.short_name || '').trim()).filter(Boolean);
  }

  unansweredQuestionCountForFamily(family) {
    return family.questions.length - Object.keys(this.answersForFamily(family.key)).length;
  }

  questionHelpText(questionKey) {
    const copy = this.i18nValue('question_help_texts') || {};
    return copy[questionKey] || null;
  }

  coveredScenariosForFamily(family) {
    const answers = this.answersForFamily(family.key);

    return Array(family.covered_scenarios).filter((rawScenario) => getCoverageScenarioStatus(family, rawScenario, answers) === 'covered')
      .map((rawScenario) => normalizeScenario(rawScenario))
      .filter((scenario) => String(scenario.title || '').trim().length > 0);
  }

  pricingTrackingPayload(family, extraPayload = {}) {
    return {
      project_license_family_key: family.key,
      project_license_family_title: family.title,
      location: typeof window !== 'undefined' ? window.location.href : '',
      ...extraPayload
    };
  }

  trackConfigurationStarted(family) {
    if (!family || this.trackedStartedFamilyKeys.has(family.key)) {
      return;
    }

    trackCatalogEvent('project_pricing_config_started', this.pricingTrackingPayload(family, {
      question_count: Array(family.questions).length
    }));
    this.trackedStartedFamilyKeys.add(family.key);
  }

  trackConfigurationCompleted(family, answers) {
    if (!family || this.trackedCompletedFamilyKeys.has(family.key)) {
      return;
    }

    const selectedOptionKeys = getSelectedOptions(family, answers).map((option) => option.key);
    const coveredScenarioTitles = Array(family.covered_scenarios)
      .filter((rawScenario) => getCoverageScenarioStatus(family, rawScenario, answers) === 'covered')
      .map((rawScenario) => normalizeScenario(rawScenario))
      .map((scenario) => String(scenario.title || '').trim())
      .filter(Boolean);

    trackCatalogEvent('project_pricing_config_completed', this.pricingTrackingPayload(family, {
      question_count: Array(family.questions).length,
      selected_option_count: selectedOptionKeys.length,
      selected_option_keys: selectedOptionKeys.join(','),
      covered_scenario_titles: coveredScenarioTitles.join(' | '),
      estimated_price_cents: getEstimatedPriceCents(family, answers)
    }));
    this.trackedCompletedFamilyKeys.add(family.key);
  }

  handleSelectFamily(familyKey) {
    const family = this.familyByKey(familyKey);
    this.trackConfigurationStarted(family);

    this.setState((prevState) => ({
      activeFamilyKey: familyKey,
      authPromptFamilyKey: prevState.authPromptFamilyKey === familyKey ? prevState.authPromptFamilyKey : null
    }));
  }

  handleCloseConfigurator() {
    const previousFamilyKey = this.state.activeFamilyKey;

    this.setState({
      activeFamilyKey: null,
      authPromptFamilyKey: null
    }, () => {
      if (previousFamilyKey) {
        this.scrollFamilyCardIntoView(previousFamilyKey);
      }
    });
  }

  handleOptionSelect(familyKey, questionKey, optionKey) {
    const family = this.familyByKey(familyKey);
    if (!family) {
      return;
    }

    const nextAnswers = {
      ...this.answersForFamily(familyKey),
      [questionKey]: optionKey
    };
    const currentQuestionIndex = questionIndex(family, questionKey);
    const maxUnlockedQuestionIndex = getMaxUnlockedWizardStepIndex(family, nextAnswers, { skipWizardResultStep: false });
    const nextWizardStepIndex = currentQuestionIndex < maxUnlockedQuestionIndex ? currentQuestionIndex + 1 : currentQuestionIndex;

    this.setState((prevState) => ({
      publicConfigurationByFamily: {
        ...prevState.publicConfigurationByFamily,
        [familyKey]: null
      },
      publicConfigurationStateByFamily: {
        ...prevState.publicConfigurationStateByFamily,
        [familyKey]: 'idle'
      },
      answersByFamily: {
        ...prevState.answersByFamily,
        [familyKey]: nextAnswers
      },
      wizardStepIndexByFamily: {
        ...prevState.wizardStepIndexByFamily,
        [familyKey]: nextWizardStepIndex
      }
    }), () => {
      if (nextWizardStepIndex === family.questions.length) {
        this.trackConfigurationCompleted(family, nextAnswers);
      }
    });
  }

  goToWizardStep(familyKey, nextIndex) {
    const family = this.familyByKey(familyKey);
    if (!family) {
      return;
    }

    const clampedStepIndex = Math.max(0, Math.min(nextIndex, this.maxUnlockedWizardStepIndexForFamily(family)));

    this.setState((prevState) => ({
      wizardStepIndexByFamily: {
        ...prevState.wizardStepIndexByFamily,
        [familyKey]: clampedStepIndex
      }
    }));
  }

  handleRestartConfiguration(familyKey) {
    this.setState((prevState) => {
      const { [familyKey]: _savedPresetToDiscard, ...remainingSavedPresets } = prevState.savedPresetByFamily;
      const { [familyKey]: _savedStateToDiscard, ...remainingSaveStates } = prevState.saveStateByFamily;
      const { [familyKey]: _configurationToDiscard, ...remainingConfigurations } = prevState.configurationByFamily;
      const { [familyKey]: _publicConfigurationToDiscard, ...remainingPublicConfigurations } = prevState.publicConfigurationByFamily;
      const { [familyKey]: _publicConfigurationStateToDiscard, ...remainingPublicConfigurationStates } = prevState.publicConfigurationStateByFamily;

      return ({
      answersByFamily: {
        ...prevState.answersByFamily,
        [familyKey]: {}
      },
      wizardStepIndexByFamily: {
        ...prevState.wizardStepIndexByFamily,
        [familyKey]: 0
      },
      saveStateByFamily: remainingSaveStates,
      savedPresetByFamily: remainingSavedPresets,
      configurationByFamily: remainingConfigurations,
      publicConfigurationByFamily: remainingPublicConfigurations,
      publicConfigurationStateByFamily: remainingPublicConfigurationStates,
      authPromptFamilyKey: prevState.authPromptFamilyKey === familyKey ? null : prevState.authPromptFamilyKey
      });
    });
  }

  handlePresetNameChange(familyKey, event) {
    this.setState((prevState) => ({
      presetNameByFamily: {
        ...prevState.presetNameByFamily,
        [familyKey]: event.target.value
      }
    }));
  }

  configurationPayloadForFamily(family) {
    return {
      ...buildConfigurationPayload(family, this.answersForFamily(family.key)),
      presetName: this.presetNameForFamily(family).toString().trim()
    };
  }

  presetPayload(configuration) {
    return {
      name: configuration.presetName || this.buildPresetName(configuration.familyTitle),
      family_key: configuration.familyKey || '',
      family_title: configuration.familyTitle || '',
      estimated_price_cents: configuration.estimatedPriceCents || 0,
      selected_option_keys: Array(configuration.selectedOptionKeys || []),
      answers: configuration.answers || {}
    };
  }

  async submitPreset(configuration, { authMode = null } = {}) {
    const payload = this.presetPayload(configuration);

    if (!this.props.savePresetPath) {
      return {
        status: 'saved',
        preset: {
          name: payload.name,
          familyKey: payload.family_key,
          familyTitle: payload.family_title,
          estimatedPriceCents: payload.estimated_price_cents,
          selectedOptionKeys: payload.selected_option_keys,
          answers: payload.answers
        }
      };
    }

    const response = await fetch(this.props.savePresetPath, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({
        preset: payload,
        auth_mode: authMode,
        return_to: this.currentReturnTo(),
        signup_context: this.props.signupContext
      })
    });

    if (!response.ok) {
      throw new Error(`Preset save failed with status ${response.status}`);
    }

    return response.json();
  }

  rememberSavedPreset(preset) {
    if (typeof window === 'undefined') {
      return;
    }

    const existingPresets = Array.isArray(window.__customerProjectLicensePresets) ? window.__customerProjectLicensePresets : [];
    window.__customerProjectLicensePresets = [preset, ...existingPresets.filter((item) => item.id !== preset.id)];
  }

  configurationStateForFamily(familyKey) {
    return this.state.publicConfigurationStateByFamily[familyKey] || 'idle';
  }

  async submitPublicConfiguration(configuration) {
    if (!this.props.createConfigurationPath) {
      return { status: 'created', configuration: null };
    }

    const response = await fetch(this.props.createConfigurationPath, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({
        configuration: {
          family_key: configuration.familyKey || '',
          selected_option_keys: Array(configuration.selectedOptionKeys || []),
          answers: configuration.answers || {}
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Configuration create failed with status ${response.status}`);
    }

    return response.json();
  }

  async ensurePublicConfiguration(configuration) {
    const familyKey = configuration.familyKey;
    const existingConfiguration = this.state.publicConfigurationByFamily[familyKey];

    if (existingConfiguration) {
      return existingConfiguration;
    }

    this.setState((prevState) => ({
      publicConfigurationStateByFamily: {
        ...prevState.publicConfigurationStateByFamily,
        [familyKey]: 'creating'
      }
    }));

    try {
      const result = await this.submitPublicConfiguration(configuration);
      const publicConfiguration = result.configuration || null;

      this.setState((prevState) => ({
        publicConfigurationByFamily: {
          ...prevState.publicConfigurationByFamily,
          [familyKey]: publicConfiguration
        },
        publicConfigurationStateByFamily: {
          ...prevState.publicConfigurationStateByFamily,
          [familyKey]: 'ready'
        }
      }));

      return publicConfiguration;
    } catch (_error) {
      this.setState((prevState) => ({
        publicConfigurationStateByFamily: {
          ...prevState.publicConfigurationStateByFamily,
          [familyKey]: 'idle'
        }
      }));

      return null;
    }
  }

  async copyTextToClipboard(value) {
    if (!value) {
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(value);
      return;
    }

    if (typeof document === 'undefined') {
      return;
    }

    const input = document.createElement('input');
    input.value = value;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }

  async handleCopyConfigurationLink(family) {
    const configuration = this.configurationPayloadForFamily(family);
    const publicConfiguration = await this.ensurePublicConfiguration(configuration);
    const detailUrl = publicConfiguration?.detailUrl || publicConfiguration?.detailPath;

    if (!detailUrl) {
      return;
    }

    await this.copyTextToClipboard(detailUrl);
    this.setState((prevState) => ({
      publicConfigurationStateByFamily: {
        ...prevState.publicConfigurationStateByFamily,
        [family.key]: 'copied'
      }
    }));
  }

  async handleOpenConfigurationDetail(family) {
    const configuration = this.configurationPayloadForFamily(family);
    const publicConfiguration = await this.ensurePublicConfiguration(configuration);
    const detailPath = publicConfiguration?.detailPath || publicConfiguration?.detailUrl;

    if (!detailPath || typeof window === 'undefined') {
      return;
    }

    window.location.assign(detailPath);
  }

  async saveConfiguration(configuration, { authMode = null } = {}) {
    const familyKey = configuration.familyKey;

    this.setState((prevState) => ({
      saveStateByFamily: {
        ...prevState.saveStateByFamily,
        [familyKey]: 'saving'
      }
    }));

    try {
      const result = await this.submitPreset(configuration, { authMode: authMode });

      if (result.status === 'auth_required' && result.redirect_to && typeof window !== 'undefined') {
        this.setState((prevState) => ({
          saveStateByFamily: {
            ...prevState.saveStateByFamily,
            [familyKey]: 'redirecting'
          }
        }));
        window.location.assign(result.redirect_to);
        return;
      }

      const savedPreset = result.preset || null;

      if (savedPreset) {
        this.rememberSavedPreset(savedPreset);
      }

      this.setState((prevState) => ({
        saveStateByFamily: {
          ...prevState.saveStateByFamily,
          [familyKey]: 'saved'
        },
        savedPresetByFamily: {
          ...prevState.savedPresetByFamily,
          [familyKey]: savedPreset
        },
        latestSavedPreset: savedPreset || prevState.latestSavedPreset,
        authPromptFamilyKey: prevState.authPromptFamilyKey === familyKey ? null : prevState.authPromptFamilyKey
      }));
    } catch (_error) {
      this.setState((prevState) => ({
        saveStateByFamily: {
          ...prevState.saveStateByFamily,
          [familyKey]: 'idle'
        }
      }));
    }
  }

  handleSaveConfigurationRequest(family) {
    const configuration = this.configurationPayloadForFamily(family);

    this.setState((prevState) => ({
      configurationByFamily: {
        ...prevState.configurationByFamily,
        [family.key]: configuration
      },
      authPromptFamilyKey: this.props.customerSignedIn ? null : family.key
    }), () => {
      if (this.props.customerSignedIn) {
        this.saveConfiguration(configuration);
      }
    });
  }

  handleAuthSaveStart(familyKey, authMode) {
    const configuration = this.state.configurationByFamily[familyKey] || (() => {
      const family = this.familyByKey(familyKey);
      return family ? this.configurationPayloadForFamily(family) : null;
    })();

    if (!configuration) {
      return;
    }

    this.saveConfiguration(configuration, { authMode: authMode });
  }

  handleAuthPromptDismiss(familyKey) {
    this.setState((prevState) => ({
      authPromptFamilyKey: prevState.authPromptFamilyKey === familyKey ? null : prevState.authPromptFamilyKey
    }));
  }

  renderQuestion(family, question, index) {
    const selectedOption = findSelectedOption(question, this.answersForFamily(family.key)[question.key]);
    const showBackLink = index > 0;
    const questionHelpText = this.questionHelpText(question.key);

    return (
      <div className='pricing-project-license-flow__step-card pricing-license-card__question' ref={this.questionCardRef}>
        <div className='pricing-project-license-flow__step-head'>
          <span className='pricing-project-license-flow__step-progress'>
            {this.i18nValue('step_x_of_y').replace('%{current}', index + 1).replace('%{total}', family.questions.length)}
          </span>
          <span className='pricing-project-license-flow__step-current'>{this.i18nValue('current_step_label')}</span>
        </div>
        <div className='pricing-license-card__question-meta'>
          <span className='pricing-license-card__question-step'>{index + 1}</span>
          <div>
            <div className='pricing-license-card__question-kicker'>{this.i18nValue('wizard_focus_label')}</div>
            <div className='pricing-license-card__question-title'>{question.title}</div>
            <p className='pricing-license-card__question-hint pricing-license-card__question-hint--lead'>{this.i18nValue('wizard_answer_hint')}</p>
            <div className={`pricing-license-card__question-selection ${selectedOption ? '' : 'pricing-license-card__question-selection--muted'}`}>
              {selectedOption ? selectedOption.summary : this.i18nValue('choose_option')}
            </div>
            {questionHelpText ? (
              <p className='pricing-project-license-flow__question-help'>{questionHelpText}</p>
            ) : null}
          </div>
        </div>
        <div className='pricing-license-card__option-row'>
          <div className='pricing-license-card__decision-prompt'>{this.i18nValue('decision_prompt')}</div>
          {question.options.map((option) => {
            const selected = this.answersForFamily(family.key)[question.key] === option.key;

            return (
              <button
                type='button'
                className={selected ? 'pricing-license-card__option pricing-license-card__option--selected' : 'pricing-license-card__option'}
                key={option.key}
                onClick={() => this.handleOptionSelect(family.key, question.key, option.key)}
              >
                <span className='pricing-license-card__option-main'>
                  <span className='pricing-license-card__option-label'>{option.label}</span>
                  {String(option.summary || '').trim() ? (
                    <span className='pricing-license-card__option-copy'>{option.summary}</span>
                  ) : null}
                </span>
                <span className='pricing-license-card__option-meta'>
                  {optionPriceLabel(family, question, option, this.answersForFamily(family.key), this.props.locale) ? (
                    <span className='pricing-license-card__option-price'>{optionPriceLabel(family, question, option, this.answersForFamily(family.key), this.props.locale)}</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
        <div className='pricing-license-card__question-actions pricing-license-card__question-actions--wizard'>
          {showBackLink ? (
            <button type='button' className='button button--ghost' onClick={() => this.goToWizardStep(family.key, index - 1)}>
              {this.i18nValue('back_to_previous_question')}
            </button>
          ) : (
            <span></span>
          )}
          <button type='button' className='button button--text-inline' onClick={() => this.handleCloseConfigurator()}>
            {this.i18nValue('back_to_license_selection')}
          </button>
        </div>
      </div>
    );
  }

  renderSelectedAnswers(family) {
    const selectedAnswerTags = this.selectedOptionsForFamily(family).map((option) => this.selectedAnswerTag(option));

    if (selectedAnswerTags.length === 0) {
      return <p className='pricing-license-card__copy'>{this.i18nValue('summary_empty')}</p>;
    }

    return (
      <div className='pricing-license-card__answer-tags'>
        {selectedAnswerTags.map((tag) => (
          <span className={`pricing-license-card__chip pricing-license-card__chip--${tag.style}`} key={`${family.key}-${tag.key}`}>
            {tag.label}
          </span>
        ))}
      </div>
    );
  }

  renderSelectedUsageList(family) {
    const selectedUsageItems = this.readableSelectedUsageItems(family);

    if (selectedUsageItems.length === 0) {
      return <p className='pricing-license-card__copy'>{this.i18nValue('summary_empty')}</p>;
    }

    return (
      <ul className='pricing-project-license-flow__plain-list'>
        {selectedUsageItems.map((item) => (
          <li key={`${family.key}-usage-${item}`}>{item}</li>
        ))}
      </ul>
    );
  }

  renderScenarioList(family, { limit = null } = {}) {
    const coveredScenarios = this.coveredScenariosForFamily(family);
    const scenarios = coveredScenarios.length > 0
      ? coveredScenarios
      : Array(family.pricing_card?.use_cases || [])
        .map((title) => ({ title: String(title || '').trim() }))
        .filter((scenario) => scenario.title.length > 0);
    const visibleScenarios = limit ? scenarios.slice(0, limit) : scenarios;

    if (visibleScenarios.length === 0) {
      return <p className='pricing-license-card__copy'>{this.i18nValue('scenario_empty')}</p>;
    }

    return (
      <ul className='pricing-license-card__coverage-list pricing-license-card__coverage-list--positive'>
        {visibleScenarios.map((scenario) => (
          <li className='pricing-license-card__coverage-item pricing-license-card__coverage-item--covered' key={`${family.key}-${scenario.title}`}>
            <div className='pricing-license-card__coverage-item-row'>
              <span>{scenario.title}</span>
            </div>
            {scenario.note ? <div className='pricing-license-card__coverage-note'>{scenario.note}</div> : null}
          </li>
        ))}
      </ul>
    );
  }

  renderSummaryPanel(family) {
    const unansweredQuestionCount = this.unansweredQuestionCountForFamily(family);

    return (
      <aside className='pricing-project-license-flow__summary-panel'>
        <div className='pricing-project-license-flow__summary-block'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('current_price')}</span>
          <div className='pricing-project-license-flow__summary-price'>{formatProjectLicensePrice(this.estimatedPriceCentsForFamily(family), this.props.locale)}</div>
        </div>

        <div className='pricing-project-license-flow__summary-block'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('summary_selection_title')}</span>
          <div className='pricing-project-license-flow__summary-family'>{family.title}</div>
          {this.renderSelectedAnswers(family)}
        </div>

        <div className='pricing-project-license-flow__summary-block'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('summary_preview_title')}</span>
          {this.renderScenarioList(family, { limit: 3 })}
        </div>

        <div className='pricing-project-license-flow__summary-block'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('summary_price_hint_title')}</span>
          <p className='pricing-license-card__copy pricing-license-card__copy--compact'>{this.i18nValue('summary_price_hint')}</p>
        </div>

        {unansweredQuestionCount > 0 ? (
          <div className='pricing-project-license-flow__summary-block'>
            <span className='pricing-license-card__section-label'>{this.i18nValue('summary_open_questions_title')}</span>
            <div className='pricing-project-license-flow__summary-open'>{this.i18nValue('summary_open_questions_count').replace('%{count}', unansweredQuestionCount)}</div>
          </div>
        ) : null}
      </aside>
    );
  }

  renderTopSaveConfirmation() {
    const savedPreset = this.latestSavedPreset();
    if (!savedPreset) {
      return null;
    }

    return (
      <div className='pricing-project-license-flow__save-confirmation pricing-license-card__save-box pricing-license-card__save-box--saved'>
        <div className='pricing-license-card__save-title'>{this.i18nValue('saved_title')}</div>
        <p className='pricing-license-card__copy'>{this.i18nValue('saved_top_copy')}</p>
        <div className='pricing-license-card__save-name'>{savedPreset.name}</div>
        <p className='pricing-license-card__copy pricing-license-card__copy--save-hint'>{this.i18nValue('saved_reuse_hint')}</p>
        <div className='pricing-license-card__save-actions'>
          {this.props.managePresetsPath ? (
            <a href={this.props.managePresetsPath} className='button'>
              {this.i18nValue('manage_preset')}
            </a>
          ) : null}
          {this.props.musicSearchPath ? (
            <a href={this.props.musicSearchPath} className='button button--ghost'>
              {this.i18nValue('find_music_for_project')}
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  renderSaveState(family) {
    const saveState = this.state.saveStateByFamily[family.key];
    const savedPreset = this.state.savedPresetByFamily[family.key];
    const publicConfigurationState = this.configurationStateForFamily(family.key);

    if (saveState === 'saved') {
      return (
        <div className='pricing-license-card__save-box pricing-license-card__save-box--saved'>
          <div className='pricing-license-card__save-title'>{this.i18nValue('saved_title')}</div>
          <p className='pricing-license-card__copy'>{this.i18nValue('saved_copy')}</p>
          <div className='pricing-license-card__save-name'>{savedPreset?.name || this.presetNameForFamily(family)}</div>
          <p className='pricing-license-card__copy pricing-license-card__copy--save-hint'>{this.i18nValue('saved_reuse_hint')}</p>
          <div className='pricing-license-card__save-actions'>
            {this.props.managePresetsPath ? (
              <a href={this.props.managePresetsPath} className='button'>
                {this.i18nValue('manage_preset')}
              </a>
            ) : null}
            <button type='button' className='button button--ghost' onClick={() => this.handleCopyConfigurationLink(family)}>
              {publicConfigurationState === 'copied' ? this.i18nValue('copy_configuration_link_done') : this.i18nValue('copy_configuration_link')}
            </button>
            <button type='button' className='button button--ghost' onClick={() => this.handleOpenConfigurationDetail(family)}>
              {this.i18nValue('open_configuration_detail')}
            </button>
            {this.props.musicSearchPath ? (
              <a href={this.props.musicSearchPath} className='button button--ghost'>
                {this.i18nValue('find_music_for_project')}
              </a>
            ) : null}
          </div>
        </div>
      );
    }

    if (this.props.customerSignedIn && saveState === 'saving') {
      return (
        <div className='pricing-license-card__save-box'>
          <div className='pricing-license-card__save-title'>{this.i18nValue('saving_title')}</div>
          <p className='pricing-license-card__copy'>{this.i18nValue('saving_copy')}</p>
        </div>
      );
    }

    return null;
  }

  renderSavePanel(family) {
    const saveState = this.state.saveStateByFamily[family.key];
    const presetName = this.presetNameForFamily(family);
    const saveDisabled = presetName.toString().trim().length === 0;
    const publicConfigurationState = this.configurationStateForFamily(family.key);
    const linkActionsDisabled = publicConfigurationState === 'creating';

    if (saveState === 'saved' || (this.props.customerSignedIn && saveState === 'saving')) {
      return this.renderSaveState(family);
    }

    return (
      <div className='pricing-license-card__save-box'>
        <div className='pricing-license-card__result-preset-name'>
          <label className='pricing-license-card__section-label' htmlFor={`preset-name-${family.key}`}>{this.i18nValue('preset_name_label')}</label>
          <input
            id={`preset-name-${family.key}`}
            type='text'
            className='input'
            value={presetName}
            onChange={(event) => this.handlePresetNameChange(family.key, event)}
            placeholder={this.i18nValue('preset_name_placeholder')}
          />
        </div>
        <p className='pricing-license-card__copy pricing-license-card__copy--save-hint'>{this.i18nValue('save_configuration_hint')}</p>
        <div className='pricing-license-card__result-actions'>
          <button type='button' className='button button--full' disabled={saveDisabled} onClick={() => this.handleSaveConfigurationRequest(family)}>
            {this.i18nValue('save_configuration')}
          </button>
          <button type='button' className='button button--ghost button--full' disabled={linkActionsDisabled} onClick={() => this.handleCopyConfigurationLink(family)}>
            {publicConfigurationState === 'copied' ? this.i18nValue('copy_configuration_link_done') : (publicConfigurationState === 'creating' ? this.i18nValue('creating_configuration_link') : this.i18nValue('copy_configuration_link'))}
          </button>
          <button type='button' className='button button--ghost button--full' disabled={linkActionsDisabled} onClick={() => this.handleOpenConfigurationDetail(family)}>
            {this.i18nValue('open_configuration_detail')}
          </button>
          <button type='button' className='button button--ghost button--full' onClick={() => this.handleRestartConfiguration(family.key)}>
            {this.i18nValue('adjust_configuration')}
          </button>
          <button type='button' className='button button--text-inline pricing-license-card__result-link' onClick={() => this.handleCloseConfigurator()}>
            {this.i18nValue('choose_other_family')}
          </button>
        </div>
      </div>
    );
  }

  renderAuthStep(family) {
    const presetName = this.presetNameForFamily(family).toString().trim() || this.buildPresetName(family.title);
    const saveState = this.state.saveStateByFamily[family.key];
    const actionsDisabled = saveState === 'saving' || saveState === 'redirecting';

    return (
      <div className='pricing-license-card__result pricing-license-card__result--auth'>
        <div className='pricing-license-card__result-title'>{this.i18nValue('auth_title')}</div>
        <p className='pricing-license-card__copy'>{this.i18nValue('auth_copy')}</p>
        <div className='pricing-license-card__result-price'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('final_project_price')}</span>
          <strong>{formatProjectLicensePrice(this.estimatedPriceCentsForFamily(family), this.props.locale)}</strong>
        </div>
        <div className='pricing-license-card__result-preset-name'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('preset_name_label')}</span>
          <div className='pricing-license-card__save-name'>{presetName}</div>
        </div>
        <div className='pricing-license-card__save-actions pricing-license-card__save-actions--stacked'>
          <button type='button' className='button' disabled={actionsDisabled} onClick={() => this.handleAuthSaveStart(family.key, 'sign_up')}>
            {this.i18nValue('sign_up_to_save')}
          </button>
          <button type='button' className='button button--ghost' disabled={actionsDisabled} onClick={() => this.handleAuthSaveStart(family.key, 'sign_in')}>
            {this.i18nValue('sign_in_to_save')}
          </button>
        </div>
        <div className='pricing-license-card__question-actions pricing-license-card__question-actions--auth'>
          <button type='button' className='pricing-license-card__wizard-link' onClick={() => this.handleAuthPromptDismiss(family.key)}>
            {this.i18nValue('back_to_result')}
          </button>
        </div>
      </div>
    );
  }

  renderResult(family) {
    return (
      <div className='pricing-license-card__result'>
        <div className='pricing-license-card__result-title'>{this.i18nValue('result_title').replace('%{family}', family.title)}</div>
        <p className='pricing-license-card__copy'>{this.i18nValue('result_copy')}</p>
        <div className='pricing-license-card__result-price'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('final_project_price')}</span>
          <strong>{formatProjectLicensePrice(this.estimatedPriceCentsForFamily(family), this.props.locale)}</strong>
        </div>
        {this.renderSavePanel(family)}
        <div className='pricing-project-license-flow__result-block'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('selected_usage')}</span>
          {this.renderSelectedUsageList(family)}
        </div>
        <div className='pricing-project-license-flow__result-block'>
          <span className='pricing-license-card__section-label'>{this.i18nValue('matching_scenarios_result')}</span>
          {this.renderScenarioList(family)}
        </div>
      </div>
    );
  }

  renderActiveConfigurator(family) {
    const currentStepIndex = this.currentWizardStepIndexForFamily(family);
    const isAuthStep = this.state.authPromptFamilyKey === family.key;
    const isResultStep = currentStepIndex === family.questions.length;
    const currentQuestion = this.currentQuestionForFamily(family);
    const progressPercentage = (isAuthStep || isResultStep)
      ? 100
      : ((currentStepIndex + 1) / family.questions.length) * 100;

    return (
      <section className='pricing-project-license-flow__configurator' ref={this.configuratorRef}>
        <div className='pricing-project-license-flow__configurator-head'>
          <div>
            <p className='pricing-selection__section-kicker margin-bottom8'>{this.i18nValue('configurator_kicker')}</p>
            <h2 className='text-like-h3 margin-bottom10'>{this.i18nValue('configurator_title').replace('%{family}', family.title)}</h2>
            <p className='margin-bottom0 text-color-secondary'>{this.i18nValue('configurator_copy')}</p>
          </div>
          <div className='pricing-project-license-flow__configurator-price'>
            <span className='pricing-license-card__section-label'>{isResultStep ? this.i18nValue('final_price') : this.i18nValue('current_price')}</span>
            <strong>{formatProjectLicensePrice(this.estimatedPriceCentsForFamily(family), this.props.locale)}</strong>
          </div>
        </div>

        <div className='pricing-license-card__wizard-head pricing-project-license-flow__wizard-head'>
          <span className='pricing-license-card__wizard-progress-copy'>
            {isAuthStep ? this.i18nValue('auth_step') : (isResultStep ? this.i18nValue('result_step') : this.i18nValue('step_x_of_y').replace('%{current}', currentStepIndex + 1).replace('%{total}', family.questions.length))}
          </span>
          <button type='button' className='button button--text-inline pricing-license-card__close-link' onClick={() => this.handleCloseConfigurator()}>
            {this.i18nValue('close_configuration')}
          </button>
        </div>
        <div className='pricing-license-card__wizard-progress-track'>
          <div className='pricing-license-card__wizard-progress-fill' style={{ width: `${progressPercentage}%` }}></div>
        </div>

        <div className='pricing-project-license-flow__configurator-layout'>
          <div className='pricing-project-license-flow__configurator-main'>
            {isAuthStep ? this.renderAuthStep(family) : (isResultStep ? this.renderResult(family) : this.renderQuestion(family, currentQuestion, currentStepIndex))}
          </div>
          <div className='pricing-project-license-flow__configurator-side'>
            {this.renderSummaryPanel(family)}
          </div>
        </div>
      </section>
    );
  }

  render() {
    const activeFamily = this.activeFamily();

    return (
      <div className='pricing-project-license-flow'>
        {this.renderTopSaveConfirmation()}
        <div className={activeFamily ? 'pricing-project-license-flow__grid pricing-project-license-flow__grid--selection-active' : 'pricing-project-license-flow__grid'}>
        {this.props.families.map((family) => (
          (() => {
            if (!this.familyCardRefs[family.key]) {
              this.familyCardRefs[family.key] = React.createRef();
            }

            return (
          <PricingLicenseCard
              key={family.key}
              cardRef={this.familyCardRefs[family.key]}
              family={family}
              locale={this.props.locale}
              managePresetsPath={this.props.managePresetsPath}
              isActive={activeFamily?.key === family.key}
              hasActiveFamily={Boolean(activeFamily)}
              savedPresets={this.savedPresetsForFamily(family.key)}
              justSavedPreset={this.justSavedPresetForFamily(family.key)}
              onSelect={() => this.handleSelectFamily(family.key)}
            />
            );
          })()
          ))}
        </div>

        {activeFamily ? this.renderActiveConfigurator(activeFamily) : null}
      </div>
    );
  }
}

PricingProjectLicenseFlow.defaultProps = {
  locale: 'de',
  families: [],
  customerSignedIn: false,
  savedPresetsByFamily: {},
  justSavedPresetId: null,
  savePresetPath: null,
  managePresetsPath: null,
  musicSearchPath: null,
  createConfigurationPath: null,
  signupContext: null,
  i18n: {
    configurator_kicker: 'Schritt 2',
    configurator_title: '%{family}-Lizenz konfigurieren',
    configurator_copy: 'Beantworten Sie jetzt die Fragen zu Ihrem Projekt. Preis und abgedeckter Scope aktualisieren sich direkt mit Ihrer Auswahl.',
    current_price: 'Aktueller Preis',
    final_price: 'Finaler Preis',
    final_project_price: 'Finaler Projektpreis',
    selected_usage: 'Abgedeckte Nutzungen',
    matching_scenarios: 'Typische passende Nutzungsszenarien',
    matching_scenarios_result: 'Typische passende Einsatzszenarien',
    summary_preview_title: 'Passende Basis für',
    summary_selection_title: 'Ihre Auswahl bisher',
    summary_price_hint_title: 'Hinweis',
    summary_price_hint: 'Der Preis aktualisiert sich mit Ihrer Auswahl.',
    summary_open_questions_title: 'Noch offen',
    summary_open_questions_count: '%{count} weitere Projektfragen',
    summary_empty: 'Ihre Auswahl erscheint hier Schritt für Schritt.',
    scenario_empty: 'Die passenden Nutzungsszenarien erscheinen nach Ihrer Auswahl.',
    wizard_focus_label: 'Aktuelle Frage',
    current_step_label: 'Aktueller Schritt',
    wizard_answer_hint: 'Wählen Sie die passende Option für Ihr Projekt.',
    decision_prompt: 'Bitte wählen Sie jetzt die passende Antwort.',
    choose_option: 'Bitte wählen Sie eine Option.',
    back: '← Vorherige Frage',
    back_to_previous_question: 'Zurück zur vorherigen Frage',
    back_to_license_selection: 'Zurück zur Lizenzwahl',
    auth_step: 'Konto wählen',
    result_step: 'Ergebnis',
    step_x_of_y: 'Schritt %{current} von %{total}',
    result_title: 'Ihre %{family}-Lizenz ist konfiguriert',
    result_copy: 'Hier sehen Sie den finalen Projektpreis und das daraus entstehende Lizenz-Preset für Ihr Projekt.',
    preset_name_label: 'Konfigurationsname',
    preset_name_placeholder: 'Zum Beispiel: Recruiting-Video Müller GmbH',
    save_configuration_hint: 'Speichern Sie Ihre Konfiguration, um sie später beim Lizenzieren eines Tracks direkt auszuwählen oder intern abzustimmen.',
    save_configuration: 'Konfiguration speichern',
    copy_configuration_link: 'Link zur Lizenz kopieren',
    copy_configuration_link_done: 'Lizenz-Link kopiert',
    creating_configuration_link: 'Lizenz-Link wird erstellt',
    open_configuration_detail: 'Lizenz-Detailseite öffnen',
    adjust_configuration: 'Lizenz anpassen',
    choose_other_family: 'Andere Lizenzfamilie wählen',
    auth_title: 'Konfiguration speichern',
    auth_copy: 'Damit wir Ihre Konfiguration dauerhaft speichern können, loggen Sie sich jetzt ein oder legen Sie kurz ein Kundenkonto an.',
    sign_up_to_save: 'Registrieren und Konfiguration speichern',
    sign_in_to_save: 'Einloggen und Konfiguration speichern',
    back_to_result: '← Zurück zum Ergebnis',
    saving_title: 'Konfiguration wird gespeichert',
    saving_copy: 'Ihre Konfiguration wird gerade in Ihrem Kundenkonto gespeichert.',
    saved_title: 'Konfiguration gespeichert',
    saved_copy: 'Diese Lizenz-Konfiguration wurde gespeichert und wird angezeigt, wenn Sie einen Track in den Warenkorb legen möchten.',
    saved_top_copy: 'Ihre Lizenz-Konfiguration wurde gespeichert und wird angezeigt, wenn Sie einen Track in den Warenkorb legen möchten.',
    saved_reuse_hint: 'Sie koennen diese Konfiguration spaeter direkt fuer passende Tracks auswaehlen.',
    manage_preset: 'Konfigurationen verwalten',
    find_music_for_project: 'Musik für dieses Projekt finden',
    family_selected_label: 'Familie gewählt',
    close_configuration: 'Konfiguration schließen',
    question_help_texts: {
      paid_budget_tier: 'Diese Auswahl beeinflusst Ihre Lizenz für bezahlte Verbreitung und Kampagnenreichweite.',
      marketing_intent: 'Je stärker Ihr Projekt in Vermarktung und Produktkommunikation hineinreicht, desto eher verändert sich der Lizenzrahmen.',
      usage_relation: 'Wichtig ist, ob Sie für sich selbst veröffentlichen oder Inhalte im Auftrag und Kontext eines Kunden einsetzen.'
    }
  }
};

let elements = document.querySelectorAll('[data-react-component=PricingProjectLicenseFlow]');

elements.forEach(function(element) {
  let reactProps = parseReactProps(element.dataset.reactProps);

  ReactDOM.render(
    <React.StrictMode>
      <PricingProjectLicenseFlow {...reactProps} />
    </React.StrictMode>,
    element
  );
});

export default PricingProjectLicenseFlow;
